import { apiGet, apiPatch, apiPost } from "./client";
import {
  createParty,
  deactivateParty,
  listParties,
  reactivateParty,
} from "./parties";

export type SupplierRow = {
  id: string;
  code: string | null;
  name: string;
  deactivatedAt: string | null;
  updatedAt: string;
  /** Open PO AP (total − paid); "0.00" when clear */
  outstanding: string;
};

export type SupplierListPage = {
  suppliers: SupplierRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type SupplierListSort = "name" | "code";

/** Supplier-role parties — plan/37 (`/parties?role=supplier`). */
export async function listSuppliers(
  companyRef: string,
  opts?: {
    includeDeactivated?: boolean;
    q?: string;
    limit?: number;
    cursor?: string | null;
    sort?: SupplierListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
): Promise<SupplierListPage> {
  const page = await listParties(companyRef, {
    role: "supplier",
    includeDeactivated: opts?.includeDeactivated,
    q: opts?.q,
    limit: opts?.limit,
    cursor: opts?.cursor,
    sort: opts?.sort,
    sortDir: opts?.sortDir,
    signal: opts?.signal,
  });
  return {
    suppliers: page.parties.map((p) => ({
      id: p.id,
      code: p.code,
      name: p.name,
      deactivatedAt: p.deactivatedAt,
      updatedAt: p.updatedAt,
      outstanding: p.outstandingAp ?? p.outstanding,
    })),
    hasMore: page.hasMore,
    nextCursor: page.nextCursor,
    total: page.total,
  };
}

export async function deactivateSupplier(
  companyRef: string,
  supplierId: string,
) {
  const res = await deactivateParty(companyRef, supplierId);
  return { supplier: res.party };
}

export async function reactivateSupplier(
  companyRef: string,
  supplierId: string,
) {
  const res = await reactivateParty(companyRef, supplierId);
  return { supplier: res.party };
}

export async function createSupplier(
  companyRef: string,
  body: { name: string; code?: string },
) {
  const res = await createParty(companyRef, {
    name: body.name,
    code: body.code,
    isCustomer: false,
    isSupplier: true,
  });
  return { supplier: { id: res.party.id, name: res.party.name } };
}

/** Identity rename — may apply or queue a change request (plan/16). */
export function renameSupplier(
  companyRef: string,
  supplierId: string,
  name: string,
) {
  return apiPatch<
    | {
        applied: true;
        supplier: { id: string; name: string };
      }
    | {
        applied: false;
        changeRequest: {
          id: string;
          status: string;
          permissionKey: string;
          category: string;
        };
      }
  >(`/c/${companyRef}/suppliers/${supplierId}`, { name });
}

export type PoListRow = {
  id: string;
  number: string;
  lifecycle: string;
  supplierName: string;
  supplierId: string;
  total: string;
  receive: string;
  pay: string;
  amountPaid: string;
  qtyReceived: string;
  qtyOrdered: string;
  expectedOn?: string | null;
  paymentDueOn?: string | null;
  /** Business / transaction date YYYY-MM-DD — plan/35 */
  docDate?: string | null;
  supplierRef?: string | null;
  updatedAt?: string | null;
};

export type PurchaseOrderListPage = {
  orders: PoListRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type PoListSort =
  | "number"
  | "supplierName"
  | "total"
  | "lifecycle"
  | "dueAt"
  | "docDate";

export function listPurchaseOrders(
  companyRef: string,
  opts?: {
    q?: string;
    limit?: number;
    cursor?: string | null;
    lifecycle?: string;
    tagId?: string;
    assignedToMe?: boolean;
    unassigned?: boolean;
    sort?: PoListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (
    opts?.lifecycle === "draft" ||
    opts?.lifecycle === "open" ||
    opts?.lifecycle === "void"
  ) {
    params.set("lifecycle", opts.lifecycle);
  }
  if (opts?.tagId) params.set("tagId", opts.tagId);
  if (opts?.assignedToMe) params.set("assignedToMe", "1");
  if (opts?.unassigned) params.set("unassigned", "1");
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<PurchaseOrderListPage>(
    `/c/${companyRef}/purchasing/orders${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export function createPurchaseOrder(
  companyRef: string,
  body: {
    supplierId: string;
    lines: Array<{ productId: string; qty: string; unitCost: string }>;
    taxRatePct?: string;
    taxPriceMode?: "inclusive" | "exclusive";
    number?: string;
    submit?: boolean;
    supplierRef?: string;
    expectedOn?: string;
    paymentDueOn?: string;
    docDate?: string;
    confirmOverBackdate?: boolean;
  },
) {
  return apiPost<{
    order: {
      id: string;
      number: string;
      total: string;
      netTotal: string;
      taxTotal: string;
      grossTotal: string;
    };
  }>(`/c/${companyRef}/purchasing/orders`, body);
}

export function patchPurchaseOrderDraft(
  companyRef: string,
  orderId: string,
  body: {
    supplierRef?: string | null;
    notes?: string | null;
    paymentDueOn?: string | null;
    expectedOn?: string | null;
    docDate?: string;
    confirmOverBackdate?: boolean;
  },
) {
  return apiPatch<{ order: { id: string; number: string } }>(
    `/c/${companyRef}/purchasing/orders/${orderId}`,
    body,
  );
}

export function getPurchaseOrder(companyRef: string, orderId: string) {
  return apiGet<{
    order: PoListRow & {
      supplierId: string;
      supplierRef: string | null;
      notes?: string | null;
      outstanding?: string;
      netTotal?: string;
      taxTotal?: string;
      grossTotal?: string;
      taxPriceMode?: string;
      taxRatePct?: string;
      supplierPrimaryPhone?: string | null;
      supplierPrimaryEmail?: string | null;
      supplierPrimaryAddress?: string | null;
      supplierNotes?: string | null;
    };
    lines: Array<{
      id: string;
      productId: string;
      productName: string;
      qtyOrdered: string;
      qtyReceived: string;
      qtyOpen: string;
      unitCost: string;
      lineTotal: string;
      lineNet?: string;
      lineTax?: string;
      lineGross?: string;
      trackLot?: boolean;
      trackSerial?: boolean;
    }>;
    receipts: Array<{
      id: string;
      number: string;
      lifecycle: string;
      supplierInvoiceRef: string | null;
      postedAt: string | null;
    }>;
    payments: Array<{
      id: string;
      number: string;
      direction: string;
      lifecycle: string;
      amount: string;
      postedAt: string | null;
    }>;
  }>(`/c/${companyRef}/purchasing/orders/${orderId}`);
}

export function getGoodsReceipt(companyRef: string, receiptId: string) {
  return apiGet<{
    receipt: {
      id: string;
      number: string;
      lifecycle: string;
      purchaseOrderId: string;
      poNumber: string;
      warehouseId: string;
      warehouseName: string;
      warehouseCode: string;
      supplierInvoiceRef: string | null;
      notes: string | null;
      postedAt: string | null;
    };
    lines: Array<{
      id: string;
      productId: string;
      productName: string;
      purchaseOrderLineId: string;
      qty: string;
      unitCost: string;
      lotCode: string | null;
      serialCode: string | null;
    }>;
  }>(`/c/${companyRef}/purchasing/receipts/${receiptId}`);
}

export function voidGoodsReceipt(
  companyRef: string,
  receiptId: string,
  body?: { idempotencyKey?: string; confirmOversell?: boolean },
) {
  return apiPost<{
    receipt: { id: string; number: string; lifecycle: string };
    replayed?: boolean;
  }>(`/c/${companyRef}/purchasing/receipts/${receiptId}/void`, body ?? {});
}

export function submitPurchaseOrder(companyRef: string, orderId: string) {
  return apiPost<{ order: { id: string; number: string; lifecycle: string } }>(
    `/c/${companyRef}/purchasing/orders/${orderId}/submit`,
  );
}

export function cancelPurchaseOrder(companyRef: string, orderId: string) {
  return apiPost<{ order: { id: string; number: string; lifecycle: string } }>(
    `/c/${companyRef}/purchasing/orders/${orderId}/cancel`,
  );
}

export function voidPurchaseOrder(companyRef: string, orderId: string) {
  return apiPost<{ order: { id: string; number: string; lifecycle: string } }>(
    `/c/${companyRef}/purchasing/orders/${orderId}/void`,
  );
}

export function postGoodsReceipt(
  companyRef: string,
  orderId: string,
  body: {
    lines: Array<{
      purchaseOrderLineId: string;
      qty: string;
      lotCode?: string;
      serialCode?: string;
    }>;
    warehouseId?: string;
    supplierInvoiceRef?: string;
    number?: string;
    idempotencyKey?: string;
    confirmOverReceive?: boolean;
  },
) {
  return apiPost<{
    receipt: { id: string; number: string };
    overReceive?: boolean;
    replayed?: boolean;
  }>(`/c/${companyRef}/purchasing/orders/${orderId}/receipts`, body);
}

export type SupplierPaymentListRow = {
  id: string;
  number: string;
  direction: string;
  lifecycle: string;
  amount: string;
  bankRef: string | null;
  postedAt: string | null;
  updatedAt: string;
  supplierId: string;
  supplierName: string;
  payMethodName: string | null;
};

export type SupplierPaymentListPage = {
  payments: SupplierPaymentListRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type SupplierPaymentListSort =
  | "number"
  | "supplierName"
  | "amount"
  | "lifecycle";

export function listSupplierPayments(
  companyRef: string,
  opts?: {
    q?: string;
    lifecycle?: "draft" | "posted" | "void";
    limit?: number;
    cursor?: string | null;
    sort?: SupplierPaymentListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.lifecycle) params.set("lifecycle", opts.lifecycle);
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<SupplierPaymentListPage>(
    `/c/${companyRef}/purchasing/payments${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export function voidSupplierPayment(
  companyRef: string,
  paymentId: string,
  body?: { idempotencyKey?: string },
) {
  return apiPost<{
    payment: { id: string; number: string; lifecycle: string };
    replayed?: boolean;
  }>(`/c/${companyRef}/purchasing/payments/${paymentId}/void`, body ?? {});
}

export function getSupplierPayment(companyRef: string, paymentId: string) {
  return apiGet<{
    payment: {
      id: string;
      number: string;
      direction: string;
      lifecycle: string;
      amount: string;
      bankRef: string | null;
      notes: string | null;
      postedAt: string | null;
      supplierId: string;
      supplierName: string;
      payMethodId: string | null;
      payMethodName: string | null;
      payMethodCode: string | null;
    };
    allocations: Array<{
      id: string;
      purchaseOrderId: string;
      amount: string;
      poNumber: string;
    }>;
  }>(`/c/${companyRef}/purchasing/payments/${paymentId}`);
}

export function postSupplierPayment(
  companyRef: string,
  body: {
    supplierId: string;
    purchaseOrderId: string;
    purchaseInvoiceId?: string;
    amount: string;
    direction?: "to_supplier" | "from_supplier";
    payMethodId?: string;
    bankRef?: string;
    idempotencyKey?: string;
    confirmOverpay?: boolean;
    confirmBypassRequireInvoice?: boolean;
  },
) {
  return apiPost<{
    payment: { id: string; number: string };
    overpay?: boolean;
    purchaseInvoiceId?: string | null;
    replayed?: boolean;
  }>(`/c/${companyRef}/purchasing/payments`, body);
}

export type ToPaySort =
  | "number"
  | "supplierName"
  | "outstanding"
  | "paymentDueOn";

export type ToReceiveSort =
  | "number"
  | "supplierName"
  | "receive"
  | "qtyReceived"
  | "expectedOn";

export type ToPayRow = PoListRow & { outstanding: string };

export function listToPay(
  companyRef: string,
  opts?: {
    q?: string;
    limit?: number;
    cursor?: string | null;
    sort?: ToPaySort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<{
    orders: ToPayRow[];
    hasMore?: boolean;
    nextCursor?: string | null;
    total?: number;
  }>(`/c/${companyRef}/purchasing/to-pay${qs ? `?${qs}` : ""}`, { signal: opts?.signal });
}

export function listToReceive(
  companyRef: string,
  opts?: {
    q?: string;
    limit?: number;
    cursor?: string | null;
    sort?: ToReceiveSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<{
    orders: PoListRow[];
    hasMore?: boolean;
    nextCursor?: string | null;
    total?: number;
  }>(`/c/${companyRef}/purchasing/to-receive${qs ? `?${qs}` : ""}`, { signal: opts?.signal });
}

export type PurchaseInvoiceListRow = {
  id: string;
  number: string;
  lifecycle: string;
  docDate: string;
  paymentDueOn: string | null;
  grossTotal: string;
  supplierId: string;
  supplierName: string;
  purchaseOrderId: string | null;
  supplierInvoiceRef: string | null;
  updatedAt: string;
};

export type PurchaseInvoiceListPage = {
  invoices: PurchaseInvoiceListRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export function listPurchaseInvoices(
  companyRef: string,
  opts?: {
    q?: string;
    lifecycle?: "draft" | "open" | "void";
    limit?: number;
    cursor?: string | null;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.lifecycle) params.set("lifecycle", opts.lifecycle);
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<PurchaseInvoiceListPage>(
    `/c/${companyRef}/purchasing/invoices${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export type PurchaseInvoiceDetail = {
  id: string;
  number: string;
  lifecycle: string;
  supplierId: string;
  supplierName: string;
  purchaseOrderId: string | null;
  purchaseOrderNumber: string | null;
  taxPriceMode: string;
  taxRatePct: string;
  discountPct: string;
  paymentDueOn: string | null;
  docDate: string;
  supplierInvoiceRef: string | null;
  supplierRef: string | null;
  notes: string | null;
  netTotal: string;
  taxTotal: string;
  grossTotal: string;
  amountPaid: string;
  outstanding: string;
  submittedAt: string | null;
  voidedAt: string | null;
  createdAt: string;
  updatedAt: string;
  supplierPrimaryPhone?: string | null;
  supplierPrimaryEmail?: string | null;
  supplierPrimaryAddress?: string | null;
  supplierNotes?: string | null;
  lines: Array<{
    id: string;
    productId: string;
    productName: string;
    description: string | null;
    qty: string;
    unitCost: string;
    discountPct: string;
    lineNet: string;
    lineTax: string;
    lineGross: string;
    purchaseOrderLineId: string | null;
  }>;
};

export function getPurchaseInvoice(companyRef: string, invoiceId: string) {
  return apiGet<{ invoice: PurchaseInvoiceDetail }>(
    `/c/${companyRef}/purchasing/invoices/${invoiceId}`,
  );
}

export function createPurchaseInvoice(
  companyRef: string,
  body: {
    supplierId?: string;
    purchaseOrderId?: string;
    lines: Array<{
      productId: string;
      qty: string | number;
      unitCost?: string | number;
      discountPct?: string | number;
      description?: string;
      purchaseOrderLineId?: string;
    }>;
    discountPct?: string | number;
    taxRatePct?: string | number;
    taxPriceMode?: "inclusive" | "exclusive";
    paymentDueOn?: string | null;
    docDate?: string;
    confirmOverBackdate?: boolean;
    supplierInvoiceRef?: string;
    supplierRef?: string;
    notes?: string;
    number?: string;
    submit?: boolean;
  },
) {
  return apiPost<{
    invoice: { id: string; number: string; lifecycle: string };
  }>(`/c/${companyRef}/purchasing/invoices`, body);
}

export function submitPurchaseInvoice(companyRef: string, invoiceId: string) {
  return apiPost<{ ok: true; lifecycle: string }>(
    `/c/${companyRef}/purchasing/invoices/${invoiceId}/submit`,
  );
}

export function voidPurchaseInvoice(companyRef: string, invoiceId: string) {
  return apiPost<{ ok: true; lifecycle: string }>(
    `/c/${companyRef}/purchasing/invoices/${invoiceId}/void`,
  );
}

export type PurchaseOrderInvoiceOpenLine = {
  purchaseOrderLineId: string;
  productId: string;
  productName: string;
  description: string | null;
  qtyOrdered: string;
  qtyInvoiced: string;
  qtyRemaining: string;
  unitCost: string;
  lineGross: string;
};

export function getPurchaseOrderInvoiceOpen(
  companyRef: string,
  orderId: string,
) {
  return apiGet<{
    order: {
      id: string;
      number: string;
      lifecycle: string;
      supplierId: string;
      supplierInvoiceRef: string | null;
    };
    lines: PurchaseOrderInvoiceOpenLine[];
  }>(`/c/${companyRef}/purchasing/orders/${orderId}/invoice-open`);
}
